import { useApolloClient, useQuery } from "@apollo/client";
import {
  faCopy,
  faEdit,
  faStickyNote,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BulmaButton, CancelButton, SubmitButton } from "Components/Button";
import { CollapsibleSection } from "Components/CollapsibleSection";
import { SmallDivider } from "Components/Divider";
import { Dividers } from "Components/Dividers";
import { Horizontal } from "Components/Horizontal";
import { ButtonIcon, SmallIconButton } from "Components/Icon";
import { InformationRow } from "Components/InformationRows";
import { LcpUserPageWrapper } from "Components/MainPageWrapper";
import { Modal } from "Components/Modal";
import { useModalFormState, useModalState } from "Components/ModalState";
import { Section } from "Components/Section";
import { LargeText } from "Components/StyledComponents";
import { WhatIsThisModal } from "Components/WhatIsThisModal";
import * as DateFns from "date-fns";
import {
  getDownloadApplicationAnswersUrl,
  getDownloadComplaintDocumentUrl,
  getDownloadLossPayeeEndorsementUrl,
  getDownloadPolicyDocumentUrl,
  getDownloadSurplusLinesDocumentUrl,
  getDiligentEffortFormUrl,
  getFeeCoverageDocumentUrl,
} from "downloadHelpers";
import {
  adminApplicationQuery,
  AdminApprovalQuoteQuery,
  ApplicationType,
  ApplicationStatus,
  updateIdeaLoanNumberMutation,
  updateAppShouldSendReminders,
} from "generated/graphql";
import { createFieldProps, useFormWithSchema } from "Inputs/FormTools";
import { Switch } from "Inputs/Switch";
import { Textbox } from "Inputs/Textbox";
import { YupDefined } from "Inputs/Tools";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useAsync, useMedia } from "react-use";
import { serverConstants } from "Shared/Constants";
import {
  Assert,
  formatDollarsAndCents,
  formatPercent,
  formatShortUIDate,
  formatYesNo,
  hasValue,
  hasValueFn,
  hv,
  iterToArray,
  no,
} from "Tools";
import { AdminAttachmentControl } from "Views/AdminAttachmentControl";
import { getQuotePath } from "Views/LcpWorkflow/QuoteSavedPage";
import * as Yup from "yup";
import { ApplicationNotFoundPage } from "../ApplicationNotFoundPage";
import { DashboardLink } from "../DashboardLink";
import { AdminPaths } from "../Paths";
import { CaseCollapsible } from "./CaseCollapsible";
import { ClaimsCollapsible } from "./ClaimsCollapsible";
import { LossPayeeCollapsible } from "./LossPayeeCollapsible";
import { Application } from "./Types";

export function ApplicationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const variables = { id: Number(id) };
  const { data, refetch } = useQuery(adminApplicationQuery, {
    variables,
  });
  const isDesktop = useMedia("(min-width: 1024px)");

  if (!data) {
    return (
      <LcpUserPageWrapper documentTitle="Application Details">
        <>Loading...</>
      </LcpUserPageWrapper>
    );
  }
  const app = data.application;
  if (!hasValue(app)) {
    return <ApplicationNotFoundPage />;
  }

  const expiredOrInactive = isExpiredOrInactive(app);

  return (
    <LcpUserPageWrapper
      documentTitle="Application Details"
      theme={!isDesktop ? undefined : { mainDivStyle: { maxWidth: "1024px" } }}
    >
      <div className="columns is-desktop">
        <div className="column" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Section
            title={app.case?.caseName ?? "Case Name: TBD"}
            className="has-background-color-shaded"
            style={{ padding: "1em" }}
          >
            <Dividers divider={<SmallDivider />}>
              <AppInfoSection app={app} />
              <CoverageInfoSection app={app} />
              <LoanNumberSection app={app} />
            </Dividers>
          </Section>
          <ApplicantCollapsible applicant={app.applicant} />
          {hasValue(app.purchaseRecord) && (
            <LossPayeeCollapsible app={app} refetch={refetch} />
          )}
          {app.case && <CaseCollapsible app={app} case={app.case} />}
        </div>
        <div className="column" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <ApplicantEligibilityCollapsible app={app} />
          {app.hasAdminApproval === true && (
            <AdminReviewCollapsible app={app} />
          )}
          {app.purchaseRecord && (
            <>
              <CommissionsCollapsible
                partner={app.partner}
                purchaseRecord={app.purchaseRecord}
              />
              <PremiumsCollapsible
                app={app}
                purchaseRecord={app.purchaseRecord}
              />
            </>
          )}
          <DocumentsCollapsible app={app} />
          <AdminAttachmentsCollapsible
            app={app}
            refetch={() => refetch(variables)}
            attachments={data.adminAttachments}
          />
          {app.claim && (
            <ClaimsCollapsible app={{ ...app, claim: app.claim }} />
          )}
          {serverConstants.ShowApprovalWorkflow &&
            app.status === ApplicationStatus.NEEDS_APPROVAL &&
            hasValue(app.adminApprovalKey) &&
            !expiredOrInactive && (
              <>
                <div className="has-text-centered">
                  <Link
                    to={AdminPaths.ApproveOrDenyPurchase({
                      adminApprovalKey: app.adminApprovalKey,
                    })}
                    className="button is-primary"
                  >
                    Approve or Deny Purchase
                  </Link>
                </div>
                <div
                  className="has-background-color-shaded"
                  style={{ padding: "1em", marginTop: 15 }}
                >
                  <p>
                    <strong>
                      Application requires review for the following reasons:
                    </strong>
                  </p>
                  <div
                    style={{ marginTop: 15 }}
                    dangerouslySetInnerHTML={{
                      __html: data.application?.reviewReasonsHtml ?? "",
                    }}
                  />
                </div>
              </>
            )}
        </div>
      </div>
      <DashboardLink />
    </LcpUserPageWrapper>
  );
}

function StatusLine({ app }: { app: Application }) {
  const history = useHistory();
  switch (app.status) {
    case ApplicationStatus.APPROVED:
      return <PurchasableQuoteLine app={app} text="Approved Quote" />;
    case ApplicationStatus.DECLINED:
      return <>Declined</>;
    case ApplicationStatus.EXPIRED_QUOTE:
      return <>Expired Quote</>;
    case ApplicationStatus.PURCHASED_POLICY:
      return (
        <>
          Purchased Policy{" "}
          {hasValue(app.case?.closedOn) ? <em>(Off Risk)</em> : <em>(Risk)</em>}
        </>
      );
    case ApplicationStatus.SAVED_QUOTE:
      return <PurchasableQuoteLine app={app} text="Saved Quote" />;
    case ApplicationStatus.NEEDS_APPROVAL:
      return (
        <>
          <span>Under Review</span>
          {serverConstants.ShowApprovalWorkflow && (
            <SmallIconButton
              icon={faEdit}
              onClick={() =>
                history.push(
                  AdminPaths.ApproveOrDenyPurchase({
                    adminApprovalKey: app.adminApprovalKey ?? no(),
                  }),
                )
              }
              buttonStyle={{ paddingRight: 0 }}
            />
          )}
        </>
      );
    case ApplicationStatus.INELIGIBLE:
      return (
        <>
          Ineligible for Quote{" "}
          <WhatIsThisModal title="Ineligible for Quote">
            <p>
              <strong>
                This application required review and was ineligible for the
                following reasons:
              </strong>
            </p>
            <div
              style={{ marginTop: 15 }}
              dangerouslySetInnerHTML={{
                __html: app.reviewReasonsHtml ?? "",
              }}
            />
          </WhatIsThisModal>
        </>
      );
    case ApplicationStatus.UNSAVED_APPLICATION:
      return <>Unsaved Application</>;
    case ApplicationStatus.CANCELED:
      return <>Canceled</>;
  }
}

function PurchasableQuoteLine({
  app,
  text,
}: {
  app: Application;
  text: string;
}) {
  return (
    <>
      <span>{text} </span>
      <FontAwesomeIcon
        icon={faCopy}
        onClick={async () => {
          const path = getQuotePath(app);
          await navigator.clipboard.writeText(
            `${window.location.protocol}//${window.location.host}${path}`,
          );
          alert("Quote link copied to clipboard");
        }}
        style={{ cursor: "pointer", padding: 3 }}
      />
    </>
  );
}

export function formatAppType(type: ApplicationType) {
  switch (type) {
    case ApplicationType.DEFENSE:
      return "Defense";
    case ApplicationType.PLAINTIFF:
      return "Plaintiff";
  }
}

export function getApplicantType(app: { type: ApplicationType }) {
  switch (app.type) {
    case ApplicationType.DEFENSE:
      return "Defendant";
    case ApplicationType.PLAINTIFF:
      return "Plaintiff";
  }
}

export function AppInfoSection(props: { app: Application }) {
  const apolloClient = useApolloClient();
  const [shouldSendReminders, setShouldSendReminders] = useState(
    props.app.shouldSendReminders,
  );
  useEffect(() => {
    setShouldSendReminders(props.app.shouldSendReminders);
  }, [props.app.shouldSendReminders]);
  const updateShouldSendRemindersResult = useAsync(async () => {
    if (shouldSendReminders === props.app.shouldSendReminders) return;
    return await apolloClient.mutate({
      mutation: updateAppShouldSendReminders,
      variables: {
        input: { securityKey: props.app.securityKey, shouldSendReminders },
      },
    });
  }, [shouldSendReminders]);
  return (
    <>
      <InformationRow label="Applicant Name">
        {props.app.applicant.fullName}
      </InformationRow>
      <InformationRow label="Company Name">
        {props.app.applicant.companyName}
      </InformationRow>
      <InformationRow label="Status">
        <StatusLine app={props.app} />
      </InformationRow>
      {props.app.status === ApplicationStatus.SAVED_QUOTE && (
        <Switch
          field={createFieldProps({
            name: "shouldSendReminders",
            value: shouldSendReminders,
            setValue: value => {
              setShouldSendReminders(value);
            },
          })}
          isLoading={updateShouldSendRemindersResult.loading}
          style={{
            textAlign: "right",
            marginBottom: "0.5em",
          }}
        >
          Send Automated Quote Reminders
        </Switch>
      )}
      {hasValue(props.app.purchaseRecord) ? (
        <InformationRow label="Purchase Date">
          {formatShortUIDate(props.app.purchaseRecord.datePurchased)}
        </InformationRow>
      ) : (
        <>
          {hasValue(props.app.dateQuoteSaved)
            ? [
                <InformationRow label="Quote Saved">
                  {formatShortUIDate(props.app.dateQuoteSaved)}
                </InformationRow>,
                hasValue(props.app.quoteAvailableUntil) && (
                  <InformationRow label="Quote Available Through">
                    {formatShortUIDate(props.app.quoteAvailableUntil)}
                  </InformationRow>
                ),
              ]
            : hasValue(props.app.dateStarted) && (
                <InformationRow label="Application Started">
                  {formatShortUIDate(props.app.dateStarted)}
                </InformationRow>
              )}
          {hasValue(props.app.dateModified) && (
            <InformationRow label="Last Modified">
              {formatShortUIDate(props.app.dateModified)}
            </InformationRow>
          )}
        </>
      )}
    </>
  );
}

export function CoverageInfoSection(props: { app: Application }) {
  return (
    <>
      <InformationRow label="Policy Number">
        <LargeText>{props.app.purchaseRecord?.policyNumber}</LargeText>
      </InformationRow>
      <InformationRow label="Policy Type">
        {formatAppType(props.app.type)}
      </InformationRow>
      <InformationRow label="Policy Limit">
        <LargeText>
          {hasValueFn(props.app.case?.policyLimit, formatDollarsAndCents)}
        </LargeText>
      </InformationRow>
      <InformationRow label="Fee Coverage">
        {hasValueFn(
          props.app.optionalCoverages?.feeCoverageLimit,
          formatDollarsAndCents,
        ) ?? "None"}
      </InformationRow>
      <InformationRow label="Date Complaint Served">
        <LargeText>
          {!props.app.case
            ? ""
            : hasValueFn(props.app.case.dateServed, dateStr =>
                formatShortUIDate(dateStr),
              ) ?? "Not Yet Served"}
        </LargeText>
      </InformationRow>
    </>
  );
}

function LoanNumberSection({ app }: { app: Application }) {
  const hasNumber = hv(app.ideaLoanNumber);
  const expiredOrInactive = isExpiredOrInactive(app);
  const apolloClient = useApolloClient();

  const form = useFormWithSchema({
    initialValues: { ideaLoanNumber: app.ideaLoanNumber ?? null },
    onSubmit: async values => {
      await apolloClient.mutate({
        mutation: updateIdeaLoanNumberMutation,
        variables: {
          input: {
            securityKey: app.securityKey,
            ideaLoanNumber: values.ideaLoanNumber,
          },
        },
      });
    },
    validationSchema: Yup.object({
      ideaLoanNumber: YupDefined.string()
        .nullable()
        .label("Idea Loan Number")
        .matches(/[A-Z]-\d{8}/, {
          message: "Please enter a valid format, e.g. X-00001234.",
        }),
    }),
  });

  const modalState = useModalFormState({ form });

  return (
    <>
      {hasNumber ? (
        <InformationRow label="Idea Loan Number">
          <strong>{app.ideaLoanNumber}</strong>{" "}
          <SmallIconButton onClick={() => modalState.open()} icon={faEdit} />
        </InformationRow>
      ) : (
        !expiredOrInactive && (
          <div className="has-text-centered">
            <BulmaButton
              className="is-primary"
              onClick={() => modalState.open()}
            >
              <span>Enter Idea Loan Number</span>
            </BulmaButton>
          </div>
        )
      )}

      <Modal
        title={`${hasNumber ? "Edit" : "Enter"} Idea Loan Number`}
        {...modalState}
        buttonRow={
          <>
            <CancelButton
              disabled={form.isSubmitting}
              onClick={() => modalState.close()}
            />
            <SubmitButton
              isSubmitting={form.isSubmitting}
              disabled={!form.dirty}
              onClick={() => modalState.submitFormAndClose()}
            />
          </>
        }
      >
        <Horizontal {...form.field.ideaLoanNumber.getLabelFromSchema()}>
          <Textbox
            fieldProps={form.field.ideaLoanNumber}
            placeholder="X-00001234"
          />
        </Horizontal>
        {hasNumber && <i>(Leave textbox blank to clear loan number.)</i>}
      </Modal>
    </>
  );
}

function ApplicantCollapsible(props: { applicant: Application["applicant"] }) {
  return (
    <CollapsibleSection
      title={<>Applicant: {props.applicant.fullName}</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      <div>{props.applicant.mailingAddress1}</div>
      <div>{props.applicant.mailingAddress2}</div>
      <div>
        {props.applicant.city}, {props.applicant.state}{" "}
        {props.applicant.zipCode}
      </div>
      <div>
        {props.applicant.phoneNumber}
        {hasValue(props.applicant.phoneExtension) &&
          ` ext. ${props.applicant.phoneExtension}`}
      </div>
      <div>{props.applicant.emailAddress}</div>
    </CollapsibleSection>
  );
}

function ApplicantEligibilityCollapsibleRow(props: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="columns is-gapless" style={{ marginBottom: "0.5em" }}>
      <div className="column">
        <strong>{props.children}</strong>
      </div>
      <div className="column">{props.label}</div>
    </div>
  );
}

function AdminReviewCollapsible({ app }: { app: Application }) {
  const modalState = useModalState();
  const isLongNote = (app.approvalNote?.length ?? 0) > 200;
  const noteToShow = `${app.approvalNote?.slice(0, 200) ?? ""}${
    isLongNote ? "..." : ""
  }`;

  return (
    <CollapsibleSection
      title={<>Admin Review</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      <InformationRow label="Approved By">{app.approverName}</InformationRow>
      <InformationRow label="Approved On">
        {formatShortUIDate(app.approvedOn ?? no())}
      </InformationRow>
      <InformationRow label="Base Premium Rate Adjustment">
        {formatYesNo(hasValue(app.basePremiumRateOverride))}
      </InformationRow>
      <InformationRow label="Notes" shouldTopAlign>
        <p>{noteToShow.length > 0 ? noteToShow : <em>(None)</em>}</p>
      </InformationRow>
      {isLongNote && (
        <>
          <p style={{ textAlign: "center" }}>
            <BulmaButton
              className="is-light"
              style={{ marginTop: 5 }}
              onClick={() => modalState.open()}
            >
              <ButtonIcon icon={faStickyNote} />
              <span>View Full Notes</span>
            </BulmaButton>
          </p>
          <Modal
            {...modalState}
            title="Admin Review Notes"
            buttonRow={[
              <CancelButton onClick={() => modalState.close()} key="Close">
                Close
              </CancelButton>,
            ]}
            theme={{ section: { overflowY: "auto" } }}
          >
            <p style={{ whiteSpace: "pre-wrap" }}>{app.approvalNote}</p>
          </Modal>
        </>
      )}
    </CollapsibleSection>
  );
}

function ApplicantEligibilityCollapsible({ app }: { app: Application }) {
  const formatYesNoTbd = (value: boolean | null | undefined) =>
    hasValueFn(value, bool => formatYesNo(bool)) ?? "TBD";
  return (
    <CollapsibleSection
      title={<>Applicant Eligibility Questions</>}
      theme={{
        header: { className: "is-secondary" },
        containerStyle: { marginTop: 0 },
      }}
    >
      <ApplicantEligibilityCollapsibleRow
        label={
          <>
            Has the First Chair Attorney, Other Attorney(s) or{" "}
            {getApplicantType(app)}(s) been found in violation of Federal Rule
            of Civil Procedure 11 or its state court equivalent?
          </>
        }
      >
        {formatYesNoTbd(app.eligibilitySurvey?.hasViolatedProcedureEleven)}
      </ApplicantEligibilityCollapsibleRow>
      <ApplicantEligibilityCollapsibleRow
        label={
          <>
            Have any of the attorneys representing the {getApplicantType(app)}
            (s) been disciplined by the bar of any state in the previous 5
            years?
          </>
        }
      >
        {formatYesNoTbd(app.eligibilitySurvey?.hasApplicantBeenDisciplined)}
      </ApplicantEligibilityCollapsibleRow>
      <ApplicantEligibilityCollapsibleRow
        label={serverConstants.SharedStrings.HasRecentLcpClaimText}
      >
        {formatYesNoTbd(app.eligibilitySurvey?.hasRecentLcpClaim)}
      </ApplicantEligibilityCollapsibleRow>
      {hasValueFn(app.eligibilitySurvey?.isDefendantTobaccoRelated, answer => (
        <ApplicantEligibilityCollapsibleRow
          label={serverConstants.SharedStrings.IsDefendantTobaccoRelatedText}
        >
          {formatYesNoTbd(answer)}
        </ApplicantEligibilityCollapsibleRow>
      ))}
      <ApplicantEligibilityCollapsibleRow
        label={serverConstants.SharedStrings.IsCaseCovidDamagesRelatedText}
      >
        {formatYesNoTbd(app.eligibilitySurvey?.isCaseCovidDamagesRelated)}
      </ApplicantEligibilityCollapsibleRow>
      {(!app.eligibilitySurvey ||
        app.eligibilitySurvey?.affirmedNoDiscovery !== null) && (
        <ApplicantEligibilityCollapsibleRow
          label={serverConstants.SharedStrings.AffirmedNoDiscoveryText}
        >
          {hasValueFn(app.eligibilitySurvey?.affirmedNoDiscovery, value =>
            value ? "I Affirm" : "Unable to Affirm",
          ) ?? "TBD"}
        </ApplicantEligibilityCollapsibleRow>
      )}
    </CollapsibleSection>
  );
}

function CommissionsCollapsible(props: {
  partner: Application["partner"];
  purchaseRecord: NonNullable<Application["purchaseRecord"]>;
}) {
  return (
    <CollapsibleSection
      title={<>Commissions</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      {hasValue(props.purchaseRecord.partnerCommission) && (
        <PartnerCommissionRows
          partner={props.partner}
          partnerCommission={props.purchaseRecord.partnerCommission}
        />
      )}
      <InformationRow
        label={`Level Commission (${formatPercent(
          props.purchaseRecord.levelCommissionRate,
          { digits: 0 },
        )})`}
      >
        {formatDollarsAndCents(props.purchaseRecord.levelCommission)}
      </InformationRow>
      {props.purchaseRecord.sociusCommission > 0 && (
        <InformationRow
          label={`Socius Commission (${formatPercent(
            props.purchaseRecord.sociusCommissionRate,
            { digits: 0 },
          )})`}
        >
          {formatDollarsAndCents(props.purchaseRecord.sociusCommission)}
        </InformationRow>
      )}
      <InformationRow
        label={`Total Commission (${formatPercent(
          props.purchaseRecord.totalCommissionRate,
          { digits: 0 },
        )})`}
      >
        <LargeText>
          {formatDollarsAndCents(props.purchaseRecord.totalCommission)}
        </LargeText>
      </InformationRow>
      <InformationRow label={"Level Net Commission"}>
        <LargeText>
          {formatDollarsAndCents(props.purchaseRecord.levelNetCommission)}
        </LargeText>
      </InformationRow>
    </CollapsibleSection>
  );
}

function PartnerCommissionRows(props: {
  partner: Application["partner"];
  partnerCommission: NonNullable<
    NonNullable<Application["purchaseRecord"]>["partnerCommission"]
  >;
}) {
  Assert.hard(!!props.partner, "app has partner commission but no partner");
  return (
    <>
      <InformationRow label="Level Partner">
        {props.partner.name}
      </InformationRow>
      <InformationRow
        label={`Partner Commission (${formatPercent(
          props.partnerCommission.rate,
          { digits: 0 },
        )})`}
      >
        {formatDollarsAndCents(props.partnerCommission.amount)}
      </InformationRow>
      <InformationRow label="Partner Commission Type">
        {props.partnerCommission.type}
      </InformationRow>
    </>
  );
}

function PremiumsCollapsible(props: {
  app: Application;
  purchaseRecord: NonNullable<Application["purchaseRecord"]>;
}) {
  const isFinanced = props.app.paymentOptions?.isFinanced === true;
  return (
    <CollapsibleSection
      title={isFinanced ? "Premiums and Down Payment" : "Premiums and Payment"}
      theme={{ header: { className: "is-secondary" } }}
    >
      <InformationRow label="Base Premium">
        {formatDollarsAndCents(props.purchaseRecord.basePremium)}
      </InformationRow>
      <InformationRow label="Base Premium Rate">
        {formatPercent(props.app.basePremiumRate)}
      </InformationRow>
      <InformationRow label="Fee Coverage Premium">
        {formatDollarsAndCents(props.purchaseRecord.feeCoveragePremium)}
      </InformationRow>
      <InformationRow label="Total Premium">
        {formatDollarsAndCents(props.purchaseRecord.premium)}
      </InformationRow>
      {props.purchaseRecord.surplusLinesTax > 0 && (
        <InformationRow label="Surplus Lines Tax">
          {formatDollarsAndCents(props.purchaseRecord.surplusLinesTax)}
        </InformationRow>
      )}
      {props.purchaseRecord.premiumTax > 0 && (
        <InformationRow label={<em>Premium Tax</em>}>
          {formatDollarsAndCents(props.purchaseRecord.premiumTax)}
        </InformationRow>
      )}
      {props.purchaseRecord.policyFeeTax > 0 && (
        <InformationRow label={<em>Policy Fee Tax</em>}>
          {formatDollarsAndCents(props.purchaseRecord.policyFeeTax)}
        </InformationRow>
      )}
      {hasValue(props.purchaseRecord.stampingFee) && (
        <InformationRow label={props.purchaseRecord.stampingFeeLabel}>
          {formatDollarsAndCents(props.purchaseRecord.stampingFee)}
        </InformationRow>
      )}
      {hasValue(props.purchaseRecord.stateSpecificTaxOrFeeName) && (
        <InformationRow label={props.purchaseRecord.stateSpecificTaxOrFeeName}>
          {formatDollarsAndCents(props.purchaseRecord.stateSpecificTaxOrFee!)}
        </InformationRow>
      )}
      <InformationRow label="Policy Fee">
        {formatDollarsAndCents(props.purchaseRecord.policyFee)}
      </InformationRow>
      <InformationRow
        label={isFinanced ? "Total Down Payment*" : "Total Payment"}
        shouldTopAlign
      >
        <LargeText>
          {formatDollarsAndCents(props.purchaseRecord.amountPaid)}
        </LargeText>
        {props.purchaseRecord.isSociusBill && (
          <div>
            <em>(to be billed by Socius Insurance)</em>
          </div>
        )}
      </InformationRow>
      <InformationRow label="Payment Date">
        {formatShortUIDate(props.purchaseRecord.datePurchased)}
      </InformationRow>
      {hasValue(props.purchaseRecord.stripeFee) && (
        <>
          <InformationRow label="Stripe Fee">
            {formatDollarsAndCents(props.purchaseRecord.stripeFee)}
          </InformationRow>
          <InformationRow label="Net">
            <LargeText>
              {formatDollarsAndCents(props.purchaseRecord.netAmountPaid)}
            </LargeText>
          </InformationRow>
        </>
      )}
      {isFinanced && (
        <div>
          <em>*This policy was financed with First Insurance Funding.</em>
        </div>
      )}
    </CollapsibleSection>
  );
}

function DocumentsCollapsible(props: { app: Application }) {
  const documents = iterToArray(function* () {
    if (props.app.purchaseRecord) {
      yield {
        name: "Policy Document",
        url: getDownloadPolicyDocumentUrl(props.app),
      };
      yield {
        name: "Application Answers",
        url: getDownloadApplicationAnswersUrl(props.app),
      };
      if (hasValue(props.app.optionalCoverages?.feeCoverageLimit)) {
        yield {
          name: "Contingency Attorney Fees",
          url: getFeeCoverageDocumentUrl(props.app),
        };
      }
    }
    if (props.app.hasComplaintDocument) {
      yield {
        name: "Complaint Document",
        url: getDownloadComplaintDocumentUrl(props.app),
      };
    }
    if (props.app.hasSurplusLinesDocument) {
      yield {
        name: "Surplus Lines Document",
        url: getDownloadSurplusLinesDocumentUrl(props.app),
      };
    }
    if (props.app.hasDiligentEffortForm) {
      yield {
        name: `${props.app.applicant.state} Diligent Effort Form`,
        url: getDiligentEffortFormUrl(props.app),
      };
    }
    if (props.app.lossPayee) {
      yield {
        name: "Loss Payee Endorsement",
        url: getDownloadLossPayeeEndorsementUrl(props.app),
      };
    }
  });
  return (
    <CollapsibleSection
      title={<>Documents ({documents.length})</>}
      theme={{ header: { className: "is-secondary" } }}
      isDisabled={documents.length === 0}
    >
      <div className="has-text-centered">
        {documents.map(d => (
          <div key={d.url}>
            <a href={d.url}>
              <strong>{d.name}</strong>
            </a>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}

function AdminAttachmentsCollapsible(props: {
  app: Application;
  attachments: AdminApprovalQuoteQuery["adminAttachments"];
  refetch: () => void;
}) {
  return (
    <CollapsibleSection
      title={<>Admin Attachments ({props.attachments?.length ?? 0})</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      <AdminAttachmentControl
        appId={props.app.id}
        refetch={props.refetch}
        attachments={props.attachments}
      />
    </CollapsibleSection>
  );
}

function isExpiredOrInactive(app: Application) {
  const lastTouchedByClient = DateFns.max([
    app.dateStarted ?? 0,
    app.dateModified ?? 0,
    app.dateQuoteSaved ?? 0,
  ]);
  const today = DateFns.startOfDay(new Date());
  const inactiveCutoff = DateFns.subDays(today, 60);
  return (
    (app.quoteAvailableUntil &&
      DateFns.isBefore(app.quoteAvailableUntil, today)) ||
    DateFns.isBefore(lastTouchedByClient, inactiveCutoff)
  );
}
